import React, { useEffect, useState } from "react";

/**
 * Magic-link request page.
 * Posts the email to /api/auth/handover/request-link and shows a neutral
 * confirmation regardless of outcome (so unauthenticated users cannot
 * enumerate member emails).
 *
 * If the URL contains ?next=<safe-path>, the requested destination is
 * persisted to localStorage under `jcc_handover_next`. MagicLinkChecking
 * reads that value after redeem and returns the user to the exact page
 * they originally tried to open (e.g. /care-rooms, /care-discussion-room/<id>).
 * This survives the user opening the email link in a new tab of the
 * same browser.
 */
const NEUTRAL =
  "If this email is linked to an active Just Care Community membership, " +
  "we'll send a one-time sign-in link shortly. Please check your Inbox and " +
  "Junk/Spam folder, and look for an email from Just Care Community.";

// Same-origin path validator. Only accepts a single-slash absolute path
// (e.g. "/care-rooms"). Rejects "//attacker.com", "https://…", empty
// strings and anything unusual — protects against open-redirect abuse.
function safeNext(raw) {
  if (typeof raw !== "string") return null;
  const v = raw.trim();
  if (!v.startsWith("/") || v.startsWith("//")) return null;
  return v;
}

export default function MagicLinkRequest() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  // On mount, if a ?next=<path> is present and safe, remember it so
  // MagicLinkChecking can redirect there after the token is redeemed.
  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const n = safeNext(p.get("next"));
      if (n) {
        window.localStorage.setItem("jcc_handover_next", n);
      }
    } catch {
      /* localStorage unavailable — user will fall back to hub buttons */
    }
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/handover/request-link`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: email.trim() }),
        }
      );
    } catch {
      /* always show the same neutral message */
    } finally {
      setSubmitted(true);
      setBusy(false);
    }
  }

  return (
    <main
      className="mx-auto max-w-md px-6 py-20"
      data-testid="magic-link-request-page"
    >
      <h1 className="text-2xl font-semibold mb-2">Sign in to Just Care Community</h1>
      <p className="text-sm text-zinc-600 mb-8">
        Enter the email address associated with your membership. We&rsquo;ll
        email you a one-time sign-in link.
      </p>

      {!submitted ? (
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <label className="block text-sm font-medium" htmlFor="email">
            Email address
          </label>
          <input
            id="email"
            type="email"
            required
            autoComplete="email"
            inputMode="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="magic-link-email-input"
            className="w-full rounded-md border border-zinc-300 px-3 py-2
                       focus:outline-none focus:ring-2 focus:ring-zinc-900"
          />
          <button
            type="submit"
            disabled={busy || !email}
            data-testid="magic-link-submit-button"
            className="w-full rounded-md bg-zinc-900 px-4 py-2 text-sm
                       font-medium text-white disabled:opacity-50"
          >
            {busy ? "Sending…" : "Email me a sign-in link"}
          </button>
        </form>
      ) : (
        <div
          className="rounded-md border border-zinc-200 bg-zinc-50 p-4 text-sm
                     text-zinc-800"
          data-testid="magic-link-neutral-confirmation"
          role="status"
        >
          {NEUTRAL}
        </div>
      )}

      <p className="mt-10 text-xs text-zinc-500">
        Magic-link sign-in is available to members whose Just Care Community
        membership is active. If you don&rsquo;t have a membership yet, please
        visit the relevant membership page.
      </p>
    </main>
  );
}
